import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Bell, 
  Shield, 
  Palette, 
  Globe, 
  Key,
  Save,
  Eye,
  EyeOff,
  Check,
  X,
  AlertTriangle,
  TestTube,
  Loader
} from 'lucide-react';

const UserSettings = () => {
  const { user: authUser, updateProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    bio: '',
    company: '',
    role: '',
    location: ''
  });

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyDigest: true,
    collaborationAlerts: true,
    language: 'en',
    timezone: 'UTC'
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // API Keys state
  const [apiKeys, setApiKeys] = useState({
    openai: '',
    anthropic: ''
  });

  const [showApiKeys, setShowApiKeys] = useState({
    openai: false,
    anthropic: false
  });

  const [apiKeyStatus, setApiKeyStatus] = useState({
    openai: { valid: null, testing: false },
    anthropic: { valid: null, testing: false }
  });

  useEffect(() => {
    if (authUser) {
      setProfile({
        fullName: authUser.user_metadata?.full_name || authUser.full_name || '',
        email: authUser.email || '',
        bio: authUser.user_metadata?.bio || '',
        company: authUser.user_metadata?.company || '',
        role: authUser.user_metadata?.role || '',
        location: authUser.user_metadata?.location || ''
      });

      // Load API keys from user metadata
      if (authUser.user_metadata?.api_keys) {
        setApiKeys({
          openai: authUser.user_metadata.api_keys.openai || '',
          anthropic: authUser.user_metadata.api_keys.anthropic || ''
        });
        setApiKeyStatus({
          openai: { valid: authUser.user_metadata.api_keys.openai_valid || null, testing: false },
          anthropic: { valid: authUser.user_metadata.api_keys.anthropic_valid || null, testing: false }
        });
      }
    }
  }, [authUser]);

  const handleProfileSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateProfile({
        data: {
          full_name: profile.fullName,
          bio: profile.bio,
          company: profile.company,
          role: profile.role,
          location: profile.location
        }
      });
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApiKeySave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // First validate the keys
      const validationResults = await validateApiKeys();
      
      await updateProfile({
        data: {
          api_keys: {
            openai: apiKeys.openai,
            anthropic: apiKeys.anthropic,
            openai_valid: validationResults.openai,
            anthropic_valid: validationResults.anthropic
          }
        }
      });
      
      setApiKeyStatus({
        openai: { valid: validationResults.openai, testing: false },
        anthropic: { valid: validationResults.anthropic, testing: false }
      });
      
      setSuccess('API keys updated successfully!');
    } catch (err) {
      setError('Failed to update API keys. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateApiKeys = async () => {
    const results = { openai: null, anthropic: null };
    
    // Validate OpenAI key
    if (apiKeys.openai) {
      try {
        const response = await fetch('/api/validate-api-key', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider: 'openai', apiKey: apiKeys.openai })
        });
        results.openai = response.ok;
      } catch (err) {
        results.openai = false;
      }
    }
    
    // Validate Anthropic key
    if (apiKeys.anthropic) {
      try {
        const response = await fetch('/api/validate-api-key', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider: 'anthropic', apiKey: apiKeys.anthropic })
        });
        results.anthropic = response.ok;
      } catch (err) {
        results.anthropic = false;
      }
    }
    
    return results;
  };

  const testApiKey = async (provider) => {
    setApiKeyStatus(prev => ({
      ...prev,
      [provider]: { ...prev[provider], testing: true }
    }));

    try {
      const response = await fetch('/api/test-api-key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          provider, 
          apiKey: apiKeys[provider],
          testMessage: 'Hello, this is a test message to verify the API key works correctly.'
        })
      });
      
      const result = await response.json();
      const isValid = response.ok && result.success;
      
      setApiKeyStatus(prev => ({
        ...prev,
        [provider]: { valid: isValid, testing: false }
      }));
      
      if (isValid) {
        setSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} API key tested successfully!`);
      } else {
        setError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} API key test failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setApiKeyStatus(prev => ({
        ...prev,
        [provider]: { valid: false, testing: false }
      }));
      setError(`Failed to test ${provider} API key: ${err.message}`);
    }
  };

  const handlePreferenceChange = (key, value) => {
    if (key === 'theme') {
      setTheme(value);
    } else {
      setPreferences(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleSecuritySave = async () => {
    if (security.newPassword !== security.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (security.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Implementation would depend on your auth service
      setSuccess('Password updated successfully!');
      setSecurity({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError('Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const SettingCard = ({ icon: Icon, title, children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100/50 dark:border-gray-700/50 p-6 ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-celadon rounded-xl flex items-center justify-center">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </div>
  );

  const InputField = ({ label, value, onChange, type = 'text', placeholder, required = false, disabled = false }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 transition-colors disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        required={required}
      />
    </div>
  );

  const ApiKeyField = ({ label, provider, value, onChange, placeholder, description }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      <div className="relative">
        <input
          type={showApiKeys[provider] ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 transition-colors pr-24 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowApiKeys(prev => ({ ...prev, [provider]: !prev[provider] }))}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            {showApiKeys[provider] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => testApiKey(provider)}
              disabled={apiKeyStatus[provider].testing}
              className="text-celadon-600 hover:text-celadon-700 p-1 disabled:opacity-50"
              title="Test API Key"
            >
              {apiKeyStatus[provider].testing ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
      {value && apiKeyStatus[provider].valid !== null && (
        <div className={`mt-2 flex items-center space-x-2 text-xs ${
          apiKeyStatus[provider].valid ? 'text-green-600' : 'text-red-600'
        }`}>
          {apiKeyStatus[provider].valid ? (
            <Check className="h-3 w-3" />
          ) : (
            <X className="h-3 w-3" />
          )}
          <span>
            {apiKeyStatus[provider].valid ? 'Valid API key' : 'Invalid API key'}
          </span>
        </div>
      )}
    </div>
  );

  const ToggleSwitch = ({ enabled, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
        {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-celadon-600' : 'bg-gray-200 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center space-x-2"
          >
            <X className="h-4 w-4" />
            <span>Back</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Account Settings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage your profile, preferences, and security settings</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
            <Check className="h-5 w-5 text-green-600" />
            <p className="text-green-800">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
            <X className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Profile Settings */}
          <SettingCard icon={User} title="Profile Information">
            <div className="space-y-4">
              <InputField
                label="Full Name"
                value={profile.fullName}
                onChange={(value) => setProfile(prev => ({ ...prev, fullName: value }))}
                placeholder="Enter your full name"
                required
              />
              
              <InputField
                label="Email"
                value={profile.email}
                onChange={() => {}} // Email typically can't be changed
                type="email"
                placeholder="your@email.com"
                disabled
              />
              
              <InputField
                label="Company"
                value={profile.company}
                onChange={(value) => setProfile(prev => ({ ...prev, company: value }))}
                placeholder="Your company name"
              />
              
              <InputField
                label="Role"
                value={profile.role}
                onChange={(value) => setProfile(prev => ({ ...prev, role: value }))}
                placeholder="Your job title"
              />
              
              <InputField
                label="Location"
                value={profile.location}
                onChange={(value) => setProfile(prev => ({ ...prev, location: value }))}
                placeholder="City, Country"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bio</label>
                <textarea
                  value={profile.bio}
                  onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell us about yourself..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 transition-colors resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <button
                onClick={handleProfileSave}
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Saving...' : 'Save Profile'}</span>
              </button>
            </div>
          </SettingCard>

          {/* API Keys Settings */}
          <SettingCard icon={Key} title="API Keys (BYOK)">
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Bring Your Own Key</h4>
                    <p className="text-xs text-blue-800 mt-1">
                      Add your own OpenAI or Anthropic API keys to use these services throughout the platform. 
                      Your keys are securely stored and only used for your requests.
                    </p>
                  </div>
                </div>
              </div>

              <ApiKeyField
                label="OpenAI API Key"
                provider="openai"
                value={apiKeys.openai}
                onChange={(value) => setApiKeys(prev => ({ ...prev, openai: value }))}
                placeholder="sk-..."
                description="Your OpenAI API key for GPT models"
              />

              <ApiKeyField
                label="Anthropic API Key"
                provider="anthropic"
                value={apiKeys.anthropic}
                onChange={(value) => setApiKeys(prev => ({ ...prev, anthropic: value }))}
                placeholder="sk-ant-..."
                description="Your Anthropic API key for Claude models"
              />

              <button
                onClick={handleApiKeySave}
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Saving...' : 'Save API Keys'}</span>
              </button>

              <div className="text-xs text-gray-500 space-y-1">
                <p>• API keys are encrypted and stored securely</p>
                <p>• Keys are only used for your requests</p>
                <p>• You can remove keys at any time</p>
                <p>• Test functionality verifies key validity</p>
              </div>
            </div>
          </SettingCard>

          {/* Notification Preferences */}
          <SettingCard icon={Bell} title="Notification Preferences">
            <div className="space-y-1">
              <ToggleSwitch
                enabled={preferences.emailNotifications}
                onChange={(value) => handlePreferenceChange('emailNotifications', value)}
                label="Email Notifications"
                description="Receive notifications via email"
              />
              
              <ToggleSwitch
                enabled={preferences.pushNotifications}
                onChange={(value) => handlePreferenceChange('pushNotifications', value)}
                label="Push Notifications"
                description="Receive browser push notifications"
              />
              
              <ToggleSwitch
                enabled={preferences.weeklyDigest}
                onChange={(value) => handlePreferenceChange('weeklyDigest', value)}
                label="Weekly Digest"
                description="Get a summary of your activity"
              />
              
              <ToggleSwitch
                enabled={preferences.collaborationAlerts}
                onChange={(value) => handlePreferenceChange('collaborationAlerts', value)}
                label="Collaboration Alerts"
                description="Notifications for team activities"
              />
            </div>
          </SettingCard>

          {/* Security Settings */}
          <SettingCard icon={Shield} title="Security & Password">
            <div className="space-y-4">
              <InputField
                label="Current Password"
                value={security.currentPassword}
                onChange={(value) => setSecurity(prev => ({ ...prev, currentPassword: value }))}
                type="password"
                placeholder="Enter current password"
              />
              
              <div className="relative">
                <InputField
                  label="New Password"
                  value={security.newPassword}
                  onChange={(value) => setSecurity(prev => ({ ...prev, newPassword: value }))}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              <InputField
                label="Confirm New Password"
                value={security.confirmPassword}
                onChange={(value) => setSecurity(prev => ({ ...prev, confirmPassword: value }))}
                type="password"
                placeholder="Confirm new password"
              />

              <button
                onClick={handleSecuritySave}
                disabled={loading || !security.currentPassword || !security.newPassword}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <Key className="h-4 w-4" />
                <span>{loading ? 'Updating...' : 'Update Password'}</span>
              </button>
            </div>
          </SettingCard>

          {/* Appearance & Language */}
          <SettingCard icon={Palette} title="Appearance & Language">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">Theme</label>
                <select
                  value={theme}
                  // onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                  onChange={() => {}}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto (System)</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {theme === 'auto' ? 'Follows your system preference' : `Using ${theme} theme`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
                <select
                  value={preferences.language}
                  onChange={(e) => handlePreferenceChange('language', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Timezone</label>
                <select
                  value={preferences.timezone}
                  onChange={(e) => handlePreferenceChange('timezone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>
            </div>
          </SettingCard>
        </div>
      </div>
    </div>
  );
};

export default UserSettings; 